import {
  Permission,
  PermissionResource,
  PermissionResourceName,
  RoleResponsePrivate,
} from '@tests/types';
import { Button, Col, Input, Row, Typography } from 'antd';
import classNames from 'classnames';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import { DrawerWrapper, FiltersItem, MultiSelect } from '@/components';
import { permissionsEntitySelector } from '@/selectors';
import { useGetPermissionsListQuery, useUpdateRoleMutation } from '@/services';
import { groupByResource } from '@/utils';

import styles from './styles.module.scss';

type RoleEditorProps = {
  onClose: () => void;
  role: RoleResponsePrivate<string>;
  rolesRefetch: () => void;
};

export const RoleEditor: React.FC<RoleEditorProps> = ({ onClose, role, rolesRefetch }) => {
  const [name, setName] = useState(role.name);
  const [permissionsState, setPermissionsState] = useState(role.permissions);

  const { isFetching: isPermissionsFetching } = useGetPermissionsListQuery(
    {
      limit: Object.keys(PermissionResource).length * 5,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const [updateBlock] = useUpdateRoleMutation();

  const handleSaveRole = async () => {
    const permissionIds = permissionsState.map(({ id }) => id);

    await updateBlock({
      id: role.id,
      name,
      permissionIds,
    });

    rolesRefetch();
    onClose();
  };

  const permissions = useSelector(permissionsEntitySelector.selectAll) || [];

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (event && value !== role?.name) {
      setName(value);
    }
  };

  const handlePermissionsChange = (options: FiltersItem[], resourceName: string) => {
    const filteredPermissions = permissionsState.filter((p) => p.resource !== resourceName);
    const fromOptionsToPermissions = options.map(({ value: id }) => {
      const permission = permissions.find((p) => p.id === id);

      return permission;
    }) as Permission<Date | string>[];

    setPermissionsState([
      ...filteredPermissions,
      ...(fromOptionsToPermissions as Permission<Date>[]),
    ]);
  };

  return (
    <DrawerWrapper
      title="Редактирование роли"
      open
      scrollActive
      handleClose={onClose}
      mobileDimension={768}
      desktopModal
      modalClassName={styles.editorWrapper}
      footer={
        <div className={styles.editBtnWrapper}>
          <Button className={classNames(styles.editBtn, styles.editCloseBtn)} onClick={onClose}>
            Отменить
          </Button>
          <Button
            className={classNames(styles.editBtn, styles.editSaveBtn)}
            onClick={handleSaveRole}
            type="primary"
          >
            Сохранить
          </Button>
        </div>
      }
    >
      <Col className={styles.editor}>
        <Row gutter={[0, 10]} className={styles.inputWrapper}>
          <Input value={name} onChange={handleChangeName} />
        </Row>
        <Row>
          <Typography.Text strong color="var(--color-white)">
            Права доступа к интерактивам
          </Typography.Text>
        </Row>
        <Row justify="center" align="middle" className={styles.filters}>
          {Object.keys(PermissionResource).map((resourceName) => (
            <Row className={styles.filterRow} key={resourceName}>
              <Typography.Text type="secondary">
                {PermissionResourceName[resourceName as keyof typeof PermissionResourceName] ||
                  resourceName}
              </Typography.Text>
              <MultiSelect
                allowClear={false}
                value={groupByResource(
                  permissionsState,
                  PermissionResource[resourceName as keyof typeof PermissionResource],
                )}
                loading={isPermissionsFetching}
                options={groupByResource(
                  permissions,
                  PermissionResource[resourceName as keyof typeof PermissionResource],
                )}
                name={resourceName}
                onChange={handlePermissionsChange}
                classname="employeeEdit"
              />
            </Row>
          ))}
        </Row>
      </Col>
    </DrawerWrapper>
  );
};
